import React, { createContext, useEffect, useState, useCallback, useRef } from "react";
import { getAllData } from "../services/api";
import {
  addItem, updateItem, deleteItem
} from "../services/item";
import {
  addStockTransaction, updateStockTransaction, deleteStockTransaction
} from "../services/stockTransactions";

// ============================================
// STEP 1: CONFIGURATION
// ============================================
const SHEET_CONFIG = {
  Items: {
    label: 'Item',
    service: null,
    sortable: true,
    idField: 'id',
  },
  StockTransactions: {
    label: 'Stock Transaction',
    service: null,
    sortable: false,
    idField: 'id',
  },
  StockHistory: {
    label: 'Stock History',
    service: null,
    readOnly: true,
    sortable: false,
    idField: 'id',
  },
};

// Assign services to config
SHEET_CONFIG.Items.service = { add: addItem, update: updateItem, delete: deleteItem };
SHEET_CONFIG.StockTransactions.service = { add: addStockTransaction, update: updateStockTransaction, delete: deleteStockTransaction };
SHEET_CONFIG.StockHistory.service = null;

// ============================================
// STEP 2: DATA CONTEXT
// ============================================
export const DataContext = createContext();

const getDataTypeLabel = (dataType) => {
  return SHEET_CONFIG[dataType]?.label || dataType;
};

const getServiceFunctions = (dataType) => {
  return SHEET_CONFIG[dataType]?.service || null;
};

const isReadOnly = (dataType) => {
  return SHEET_CONFIG[dataType]?.readOnly || false;
};

const isSortable = (dataType) => {
  return SHEET_CONFIG[dataType]?.sortable || false;
};

const getIdField = (dataType) => {
  return SHEET_CONFIG[dataType]?.idField || 'id';
};

// Sort helper
const sortByName = (dataArray) => {
  if (!Array.isArray(dataArray)) return dataArray;
  return [...dataArray].sort((a, b) =>
    a.name?.localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  );
};

export const DataProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const initialLoadRef = useRef(false);

  // ============================================
  // INITIALIZE DATA STATE
  // ============================================
  useEffect(() => {
    const initialData = {};
    Object.keys(SHEET_CONFIG).forEach(key => {
      initialData[key] = [];
    });
    setData(initialData);
  }, []);

  // ============================================
  // UPDATE DATA IN STATE (Helper)
  // ============================================
  const updateDataInState = useCallback((dataType, updater) => {
    setData(prev => {
      const currentData = prev[dataType] || [];
      const updatedData = updater(currentData);
      const finalData = isSortable(dataType) ? sortByName(updatedData) : updatedData;
      return { ...prev, [dataType]: finalData };
    });
    setLastUpdated(new Date().toISOString());
  }, []);

  // ============================================
  // GET ALL DATA
  // ============================================
  const loadAllData = useCallback(async (action = 'getAll', filters = {}) => {
    setLoading(true);
    try {
      console.log(`📤 Loading all data with action: ${action}`, filters);

      const response = await getAllData(action);
      console.log('📥 All data response:', response);

      if (response?.success) {
        const fetchedData = response.data || {};

        setData(prev => {
          const newData = { ...prev };
          Object.keys(SHEET_CONFIG).forEach(key => {
            if (fetchedData[key] !== undefined) {
              let records = fetchedData[key];
              if (isSortable(key) && Array.isArray(records)) {
                records = sortByName(records);
              }
              newData[key] = records;
            }
          });
          return newData;
        });

        setLastUpdated(new Date().toISOString());
        console.log('✅ All data loaded successfully');

        return {
          success: true,
          data: fetchedData,
          message: 'Data loaded successfully'
        };
      } else {
        throw new Error(response?.message || 'Failed to load data');
      }
    } catch (error) {
      console.error('❌ Error loading all data:', error);
      return {
        success: false,
        error: error.message,
        message: 'Failed to load data'
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAllData = useCallback(async () => {
    return await loadAllData('getAll');
  }, [loadAllData]);

  // ============================================
  // GENERIC CRUD OPERATIONS WITH STATE UPDATE
  // ============================================

  /**
   * Generic add function - updates state immediately
   */
  const addRecord = useCallback(async (dataType, itemData) => {
    setLoading(true);
    try {
      if (isReadOnly(dataType)) {
        throw new Error(`${dataType} is read-only. Cannot add records.`);
      }

      const service = getServiceFunctions(dataType);
      if (!service || !service.add) {
        throw new Error(`No add service found for ${dataType}`);
      }

      console.log(`Adding ${dataType}:`, itemData);
      const response = await service.add(itemData);
      console.log(`${dataType} add response:`, response);

      if (response?.success) {
        const newItem = response.data || itemData;

        // Generate temporary ID if not provided
        if (!newItem.id) {
          newItem.id = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }

        // Update state immediately
        updateDataInState(dataType, (currentData) => {
          const idField = getIdField(dataType);
          const exists = currentData.some(item => item[idField] === newItem[idField]);
          if (exists) {
            return currentData.map(item =>
              item[idField] === newItem[idField] ? { ...item, ...newItem } : item
            );
          }
          return [...currentData, newItem];
        });

        return {
          success: true,
          data: newItem,
          message: `${getDataTypeLabel(dataType)} added successfully`
        };
      } else {
        throw new Error(response?.error || `Failed to add ${dataType}`);
      }
    } catch (error) {
      console.error(`Error adding ${dataType}:`, error);
      return {
        success: false,
        error: error.message,
        message: `Failed to add ${getDataTypeLabel(dataType)}`
      };
    } finally {
      setLoading(false);
    }
  }, [updateDataInState]);

  /**
   * Generic update function - updates state immediately
   */
  const updateRecord = useCallback(async (dataType, id, itemData) => {
    setLoading(true);
    try {
      if (isReadOnly(dataType)) {
        throw new Error(`${dataType} is read-only. Cannot update records.`);
      }

      const service = getServiceFunctions(dataType);
      if (!service || !service.update) {
        throw new Error(`No update service found for ${dataType}`);
      }

      console.log(`Updating ${dataType} ID ${id}:`, itemData);
      const response = await service.update(id, itemData);
      console.log(`${dataType} update response:`, response);

      if (response?.success) {
        const updatedItem = response.data || itemData;

        // Update state immediately
        updateDataInState(dataType, (currentData) => {
          const idField = getIdField(dataType);
          return currentData.map(item =>
            item[idField] === id ? { ...item, ...updatedItem } : item
          );
        });

        return {
          success: true,
          data: updatedItem,
          message: `${getDataTypeLabel(dataType)} updated successfully`
        };
      } else {
        throw new Error(response?.error || `Failed to update ${dataType}`);
      }
    } catch (error) {
      console.error(`Error updating ${dataType}:`, error);
      return {
        success: false,
        error: error.message,
        message: `Failed to update ${getDataTypeLabel(dataType)}`
      };
    } finally {
      setLoading(false);
    }
  }, [updateDataInState]);

  /**
   * Generic delete function - updates state immediately
   */
  const deleteRecord = useCallback(async (dataType, id) => {
    setLoading(true);
    try {
      if (isReadOnly(dataType)) {
        throw new Error(`${dataType} is read-only. Cannot delete records.`);
      }

      const service = getServiceFunctions(dataType);
      if (!service || !service.delete) {
        throw new Error(`No delete service found for ${dataType}`);
      }

      console.log(`Deleting ${dataType} ID ${id}`);
      const response = await service.delete(id);

      if (response?.success) {
        // Update state immediately - remove the item
        updateDataInState(dataType, (currentData) => {
          const idField = getIdField(dataType);
          return currentData.filter(item => item[idField] !== id);
        });

        return {
          success: true,
          message: `${getDataTypeLabel(dataType)} deleted successfully`
        };
      } else {
        throw new Error(response?.error || `Failed to delete ${dataType}`);
      }
    } catch (error) {
      console.error(`Error deleting ${dataType}:`, error);
      return {
        success: false,
        error: error.message,
        message: `Failed to delete ${getDataTypeLabel(dataType)}`
      };
    } finally {
      setLoading(false);
    }
  }, [updateDataInState]);

  /**
 * Special handler for stock transactions
 * Updates both StockTransactions and Items state
 */
  const handleStockTransaction = useCallback(async (transactionData) => {
    setLoading(true);
    try {
      console.log('Processing stock transaction:', transactionData);

      // Call the API
      const response = await addStockTransaction(transactionData);
      console.log('Stock transaction response:', response);

      if (response?.success) {
        const result = response.data;

        // 1. Update StockTransactions state
        const transactionRecord = {
          id: result.transaction?.id || result.id || `temp_${Date.now()}`,
          itemCode: transactionData.itemCode,
          itemName: transactionData.itemName,
          size: transactionData.size,
          operation: transactionData.operation,
          totalWeight: transactionData.totalWeight,
          numberOfPackets: transactionData.numberOfPackets,
          timestamp: result.transaction?.timestamp || result.timestamp || new Date().toISOString(),
        };

        updateDataInState('StockTransactions', (currentData) => {
          return [...currentData, transactionRecord];
        });

        // 2. Update Items state with new stock values
        // Check if currentStock is provided in response
        if (result.currentStock) {
          console.log('Updating Items state with new stock values:', result.currentStock);
          updateDataInState('Items', (currentData) => {
            return currentData.map(item => {
              // Find the item by code and size
              if (item.itemCode === transactionData.itemCode &&
                item.size === transactionData.size) {
                return {
                  ...item,
                  totalWeight: result.currentStock.totalWeight,
                  numberOfPackets: result.currentStock.numberOfPackets,
                  lastUpdated: new Date().toISOString()
                };
              }
              return item;
            });
          });

          console.log('✅ Updated Items state with new stock values:', result.currentStock);
        } else {
          // If currentStock is not provided, we need to calculate and update the stock ourselves
          console.log('⚠️ currentStock not provided in response, calculating manually...');

          // Get current items from state
          setData(prevData => {
            const currentItems = prevData.Items || [];
            const updatedItems = currentItems.map(item => {
              if (item.itemCode === transactionData.itemCode &&
                item.size === transactionData.size) {

                const currentPackets = item.numberOfPackets || 0;
                const currentWeight = item.totalWeight || 0;
                const newPackets = transactionData.operation === 'IN'
                  ? currentPackets + transactionData.numberOfPackets
                  : currentPackets - transactionData.numberOfPackets;
                const newWeight = transactionData.operation === 'IN'
                  ? currentWeight + transactionData.totalWeight
                  : currentWeight - transactionData.totalWeight;

                console.log(`Manual calculation: ${currentPackets} -> ${newPackets} packets, ${currentWeight} -> ${newWeight} g`);

                return {
                  ...item,
                  totalWeight: Math.max(0, newWeight),
                  numberOfPackets: Math.max(0, newPackets),
                  lastUpdated: new Date().toISOString()
                };
              }
              return item;
            });

            return {
              ...prevData,
              Items: isSortable('Items') ? sortByName(updatedItems) : updatedItems
            };
          });

          console.log('✅ Updated Items state manually with new stock values');
        }

        // 3. If updatedItem is returned, update or add it
        if (result.updatedItem) {
          updateDataInState('Items', (currentData) => {
            const idField = getIdField('Items');
            const exists = currentData.some(item => item[idField] === result.updatedItem.id);
            if (exists) {
              return currentData.map(item =>
                item[idField] === result.updatedItem.id ? { ...item, ...result.updatedItem } : item
              );
            } else {
              return [...currentData, result.updatedItem];
            }
          });
        }

        // 4. If stock history is returned, update it too
        if (result.history) {
          updateDataInState('StockHistory', (currentData) => {
            const historyRecord = {
              id: result.history.id || `hist_${Date.now()}`,
              ...result.history,
              timestamp: result.history.timestamp || new Date().toISOString(),
            };
            return [historyRecord, ...currentData];
          });
        }

        return {
          success: true,
          data: result,
          message: `Stock ${transactionData.operation === 'IN' ? 'added' : 'removed'} successfully`
        };
      } else {
        throw new Error(response?.message || 'Failed to process stock transaction');
      }
    } catch (error) {
      console.error('Error processing stock transaction:', error);
      return {
        success: false,
        error: error.message,
        message: 'Failed to process stock transaction'
      };
    } finally {
      setLoading(false);
    }
  }, [updateDataInState]);

  /**
   * Batch update - for when you need to update multiple items at once
   */
  const batchUpdate = useCallback(async (updates) => {
    setLoading(true);
    try {
      const results = [];

      for (const update of updates) {
        const { dataType, action, id, data } = update;
        let result;

        switch (action) {
          case 'add':
            result = await addRecord(dataType, data);
            break;
          case 'update':
            result = await updateRecord(dataType, id, data);
            break;
          case 'delete':
            result = await deleteRecord(dataType, id);
            break;
          default:
            throw new Error(`Unknown action: ${action}`);
        }

        results.push(result);
      }

      return {
        success: true,
        results,
        message: 'Batch update completed'
      };
    } catch (error) {
      console.error('Error in batch update:', error);
      return {
        success: false,
        error: error.message,
        message: 'Batch update failed'
      };
    } finally {
      setLoading(false);
    }
  }, [addRecord, updateRecord, deleteRecord]);

  // ============================================
  // GENERIC GET FUNCTIONS
  // ============================================
  const getRecords = useCallback((dataType, filterFn = null) => {
    const records = data[dataType] || [];
    return filterFn ? records.filter(filterFn) : records;
  }, [data]);

  // ============================================
  // AUTO-GENERATED SHEET FUNCTIONS
  // ============================================
  const sheetFunctions = {};
  Object.keys(SHEET_CONFIG).forEach(key => {
    const isReadOnlySheet = isReadOnly(key);

    sheetFunctions[`get${key}`] = (filterFn = null) => getRecords(key, filterFn);

    if (!isReadOnlySheet) {
      sheetFunctions[`add${key}`] = (itemData) => addRecord(key, itemData);
      sheetFunctions[`update${key}`] = (id, itemData) => updateRecord(key, id, itemData);
      sheetFunctions[`delete${key}`] = (id) => deleteRecord(key, id);
    }
  });

  // ============================================
  // SEARCH FUNCTION
  // ============================================
  const searchData = useCallback((searchTerm) => {
    const results = {};
    const searchLower = searchTerm.toLowerCase();

    Object.keys(data).forEach(dataType => {
      if (Array.isArray(data[dataType])) {
        results[dataType] = data[dataType].filter(item => {
          return Object.values(item).some(value =>
            String(value).toLowerCase().includes(searchLower)
          );
        });
      }
    });

    return results;
  }, [data]);

  // ============================================
  // GET STATISTICS
  // ============================================
  const getStats = useCallback(() => {
    const stats = {};
    Object.keys(data).forEach(key => {
      stats[key.toLowerCase()] = (data[key] || []).length;
    });
    stats.lastUpdated = lastUpdated;
    return stats;
  }, [data, lastUpdated]);

  // ============================================
  // INITIAL LOAD
  // ============================================
  useEffect(() => {
    const loadData = async () => {
      if (initialLoadRef.current) return;
      initialLoadRef.current = true;

      try {
        await loadAllData('getAll');
        console.log("✅ All data loaded successfully on initial load");
      } catch (error) {
        console.error("❌ Error loading data:", error);
      }
    };

    loadData();
  }, [loadAllData]);

  // ============================================
  // CONTEXT VALUE
  // ============================================
  const value = {
    loading,
    lastUpdated,
    ...data,
    loadAllData,
    refreshAllData,
    addRecord,
    updateRecord,
    deleteRecord,
    getRecords,
    handleStockTransaction,
    batchUpdate,
    ...sheetFunctions,
    searchData,
    getStats,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

// ============================================
// CUSTOM HOOKS
// ============================================

export const useData = () => {
  const context = React.useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const useSheetData = (sheetName) => {
  const context = useData();
  const data = context[sheetName] || [];
  const loading = context.loading;
  const getFn = context[`get${sheetName}`] || (() => data);
  const addFn = context[`add${sheetName}`] || null;
  const updateFn = context[`update${sheetName}`] || null;
  const deleteFn = context[`delete${sheetName}`] || null;

  return {
    data,
    loading,
    get: getFn,
    add: addFn,
    update: updateFn,
    delete: deleteFn,
  };
};

export const useItems = () => useSheetData('Items');

export const useStockTransactions = () => {
  const context = useData();
  const { data, loading, get, update, delete: deleteFn } = useSheetData('StockTransactions');

  const handleAddStock = useCallback(async (transactionData) => {
    return await context.handleStockTransaction(transactionData);
  }, [context]);

  return {
    data,
    loading,
    get,
    add: handleAddStock,
    update,
    delete: deleteFn,
  };
};

export const useStockHistory = () => {
  const { data, loading, get } = useSheetData('StockHistory');
  return {
    history: data,
    loading,
    getHistory: get,
  };
};

export const useAllDataLoader = () => {
  const { loading, lastUpdated, loadAllData, refreshAllData } = useData();
  return { loading, lastUpdated, loadAllData, refreshAllData };
};

export const useDataSearch = () => {
  const { searchData } = useData();
  return { searchData };
};

export default DataProvider;